import { useMemo, useState } from 'react'
import { Box, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine
} from 'recharts'
import { DashboardCard, SectionTitle } from '../../../../../ui'
import { formatCLPFull, parseDisplayNumber } from '../utils/visionDelNegocio.utils'

const COLOR_MUTED = 'rgb(var(--muted))'
const COLOR_GRID = 'rgb(var(--border))'
const COLOR_INCOME = '#22B5B2'
const COLOR_EXPENSE = '#E4001B'
const COLOR_F29 = '#F59E0B'
const COLOR_CASH_PROJECTION = '#22B5B2'
const COLOR_CASH_PROJECTION_LINE_POSITIVE = '#2bb3b1'
const COLOR_CASH_PROJECTION_LINE_NEGATIVE = '#d90429'
const COLOR_CASH_PROJECTION_POSITIVE = 'rgb(var(--success))'
const COLOR_CASH_PROJECTION_NEGATIVE = 'rgb(var(--danger))'
const COLOR_OVERDUE_RECEIVABLES = 'rgb(var(--success))'
const COLOR_OVERDUE_PAYABLES = 'rgb(var(--danger))'
const DEFAULT_CASH_FLOW_ADJUSTMENTS = {
  ingresos: true,
  egresos: true,
  liquido: true,
  previred: true,
  f29: true,
}

const FILTER_OPTIONS = [
  { key: 'ingresos', label: 'Ventas', color: COLOR_INCOME },
  { key: 'egresos', label: 'Gastos', color: COLOR_EXPENSE },
  { key: 'liquido', label: 'Sueldo líquido', color: COLOR_EXPENSE },
  { key: 'previred', label: 'Previred', color: COLOR_EXPENSE },
  { key: 'f29', label: 'F29', color: COLOR_F29 },
]

const formatCompactMoney = value => {
  const n = Number(value || 0)

  if (Math.abs(n) >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1)}M`
  }

  if (Math.abs(n) >= 1_000) {
    return `${(n / 1_000).toFixed(0)}K`
  }

  return `${n}`
}

const formatShortDate = value => {
  if (!value) return '—'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  return date.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
  })
}

const XAxisWeekTick = ({ x, y, payload, isMobile }) => (
  <g transform={`translate(${x},${y})`}>
    <text textAnchor='middle' fill={COLOR_MUTED} fontSize={isMobile ? 10 : 11} fontWeight={700}>
      <tspan x='0' dy='14'>
        Semana
      </tspan>
      <tspan x='0' dy='16' fontWeight={600}>
        {formatShortDate(payload?.value)}
      </tspan>
    </text>
  </g>
)

const formatMoney = value => `$ ${formatCLPFull(parseDisplayNumber(value))}`

const toNumber = value => Number(value) || 0

const TooltipRow = ({ label, value, color, valueColor = 'text-text' }) => (
  <div className='flex items-center gap-2'>
    <span
      className='inline-block h-2.5 w-2.5 rounded-full'
      style={{ backgroundColor: color }}
    />
    <span className='font-semibold' style={{ color }}>
      {label}:
    </span>
    <span className={`font-medium ${valueColor}`}>{formatMoney(value)}</span>
  </div>
)

const ProjectionDot = ({ cx, cy, payload, r = 4 }) => {
  if (cx == null || cy == null) return null

  const value = toNumber(payload?.presupuestoCajaProyectada)
  const color = value < 0
    ? COLOR_CASH_PROJECTION_LINE_NEGATIVE
    : COLOR_CASH_PROJECTION_LINE_POSITIVE

  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={color}
      stroke={color}
    />
  )
}

const CustomTooltip = ({
  active,
  payload,
  includeIngresos,
  includeEgresos,
  includeLiquido,
  includePrevired,
  includeF29,
}) => {
  if (!active || !payload?.length) return null

  const raw = payload[0]?.payload || {}
  const presupuestoCajaProyectada = parseDisplayNumber(raw.presupuestoCajaProyectada)
  const f29 = toNumber(raw.f29Dinamico)
  const presupuestoCajaColor = presupuestoCajaProyectada >= 0
    ? COLOR_CASH_PROJECTION_POSITIVE
    : COLOR_CASH_PROJECTION_NEGATIVE

  return (
    <div className='rounded-3xl border border-border bg-surface px-4 py-3 text-sm shadow-card'>
      <div className='mb-1 text-base font-extrabold text-text'>
        Semana {raw.name || '—'}
      </div>
      <div className='mb-3 text-xs font-medium text-muted'>
        {raw.weekStart || '—'} a {raw.weekEnd || '—'}
      </div>

      <div className='space-y-1.5'>
        <TooltipRow label='Saldo en caja' value={raw.saldoEnCaja} color={COLOR_CASH_PROJECTION} />
        {includeIngresos ? (
          <TooltipRow label='Ventas' value={raw.ingresosDinamicos} color={COLOR_INCOME} />
        ) : null}
        {includeEgresos ? (
          <TooltipRow label='Gastos' value={raw.egresosProveedorDinamicos} color={COLOR_EXPENSE} />
        ) : null}
        {includeLiquido ? (
          <TooltipRow label='Sueldo líquido' value={raw.liquidoSeleccionado} color={COLOR_EXPENSE} />
        ) : null}
        {includePrevired ? (
          <TooltipRow label='Previred' value={raw.previredSeleccionado} color={COLOR_EXPENSE} />
        ) : null}
        {includeF29 && f29 > 0 ? (
          <TooltipRow label='F29' value={f29} color={COLOR_F29} />
        ) : null}
        <TooltipRow
          label='Presupuesto caja proyectada'
          value={presupuestoCajaProyectada}
          color={presupuestoCajaColor}
          valueColor={presupuestoCajaProyectada >= 0 ? 'text-success' : 'text-danger'}
        />
      </div>
    </div>
  )
}

export const CashFlowDueLineChart = ({
  data = [],
  title = 'Flujo de caja por vencimiento',
  vencidoPorCobrar = 0,
  vencidoPorPagar = 0,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [selectedAdjustments, setSelectedAdjustments] = useState(DEFAULT_CASH_FLOW_ADJUSTMENTS)
  const includeIngresos = selectedAdjustments.ingresos
  const includeEgresos = selectedAdjustments.egresos
  const includeLiquido = selectedAdjustments.liquido
  const includePrevired = selectedAdjustments.previred
  const includeF29 = selectedAdjustments.f29

  const handleToggleFilter = key => {
    setSelectedAdjustments(prev => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const chartData = useMemo(() => {
    const sortedData = [...(Array.isArray(data) ? data : [])].sort(
      (a, b) => new Date(a?.weekStart).getTime() - new Date(b?.weekStart).getTime()
    )

    let saldoActual = toNumber(sortedData?.[0]?.saldoEnCaja)

    const calculatedData = sortedData.map(item => {
      const ingresos = toNumber(item?.ingresos)
      const egresos = toNumber(item?.egresos)
      const liquido = toNumber(item?.liquido)
      const previred = toNumber(item?.previred)
      const f29 = toNumber(item?.f29)
      const ingresosDinamicos = includeIngresos ? ingresos : 0
      const egresosProveedorDinamicos = includeEgresos ? egresos : 0
      const liquidoDinamico = includeLiquido ? liquido : 0
      const previredDinamico = includePrevired ? previred : 0
      const f29Dinamico = includeF29 ? f29 : 0
      const egresosDinamicos =
        egresosProveedorDinamicos +
        liquidoDinamico +
        previredDinamico +
        f29Dinamico
      const presupuestoCajaProyectada =
        saldoActual + ingresosDinamicos - egresosDinamicos
      const shouldRenderPoint =
        ingresosDinamicos > 0 ||
        egresosProveedorDinamicos > 0 ||
        liquidoDinamico > 0 ||
        previredDinamico > 0 ||
        f29Dinamico > 0

      const chartItem = {
        ...item,
        key: item?.key,
        name: item?.name,
        weekStart: item?.weekStart,
        weekEnd: item?.weekEnd,
        monthKey: item?.monthKey,
        ingresos,
        liquido,
        previred,
        f29,
        f29Dinamico,
        egresos,
        ingresosDinamicos,
        egresosProveedorDinamicos,
        liquidoSeleccionado: liquidoDinamico,
        previredSeleccionado: previredDinamico,
        egresosDinamicos,
        saldoEnCaja: saldoActual,
        presupuestoCajaProyectada,
        shouldRenderPoint,
      }

      saldoActual = presupuestoCajaProyectada

      return chartItem
    })

    return calculatedData.filter(item => item.shouldRenderPoint)
  }, [data, includeIngresos, includeEgresos, includeLiquido, includePrevired, includeF29])

  const hasData = chartData.length > 0
  const projectionValues = chartData.map(item => toNumber(item.presupuestoCajaProyectada))
  const minProjection = Math.min(...projectionValues, 0)
  const maxProjection = Math.max(...projectionValues, 0)
  const zeroOffset = maxProjection <= 0
    ? 0
    : minProjection >= 0
      ? 100
      : (maxProjection / (maxProjection - minProjection)) * 100

  return (
    <DashboardCard className='flex w-full min-w-0 flex-col justify-center'>
      <div className='flex justify-center pb-4 sm:pb-6'>
        <SectionTitle className='text-center'>{title}</SectionTitle>
      </div>

      <div className='mb-4 flex flex-col gap-3 rounded-2xl border border-border bg-border/10 px-3 py-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {FILTER_OPTIONS.map(option => {
            const checked = selectedAdjustments[option.key]

            return (
              <label
                key={option.key}
                className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  checked
                    ? 'border-transparent bg-surface text-text shadow-sm'
                    : 'border-border bg-transparent text-muted'
                }`}
              >
                <input
                  type='checkbox'
                  checked={checked}
                  onChange={() => handleToggleFilter(option.key)}
                  className='h-3.5 w-3.5 accent-current'
                  style={{ color: option.color }}
                />
                <span>{option.label}</span>
              </label>
            )
          })}
        </div>
        <div className='grid grid-cols-1 gap-2 text-right text-xs sm:grid-cols-2 sm:gap-4'>
          <div>
            <div className='font-semibold uppercase tracking-wide' style={{ color: COLOR_OVERDUE_RECEIVABLES }}>
              Vencido por cobrar
            </div>
            <div className='font-bold text-text'>{formatMoney(vencidoPorCobrar)}</div>
          </div>
          <div>
            <div className='font-semibold uppercase tracking-wide' style={{ color: COLOR_OVERDUE_PAYABLES }}>
              Vencido por pagar
            </div>
            <div className='font-bold text-text'>{formatMoney(vencidoPorPagar)}</div>
          </div>
        </div>
      </div>

      {hasData ? (
        <Box sx={{ width: '100%', height: isMobile ? 260 : 320 }}>
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart
              data={chartData}
              margin={isMobile ? { top: 12, right: 8, left: 0, bottom: 18 } : { top: 12, right: 16, left: 0, bottom: 22 }}
            >
              <defs>
                <linearGradient id='presupuestoCajaGradient' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={COLOR_CASH_PROJECTION_LINE_POSITIVE} />
                  <stop offset={`${zeroOffset}%`} stopColor={COLOR_CASH_PROJECTION_LINE_POSITIVE} />
                  <stop offset={`${zeroOffset}%`} stopColor={COLOR_CASH_PROJECTION_LINE_NEGATIVE} />
                  <stop offset='100%' stopColor={COLOR_CASH_PROJECTION_LINE_NEGATIVE} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray='3 3' vertical={false} stroke={COLOR_GRID} />
              <ReferenceLine y={0} stroke='#999' strokeDasharray='4 4' />
              <XAxis
                dataKey='weekStart'
                tick={<XAxisWeekTick isMobile={isMobile} />}
                axisLine={false}
                tickLine={false}
                height={58}
                interval={0}
                minTickGap={8}
                tickMargin={12}
              />
              <YAxis
                tick={{ fontSize: isMobile ? 11 : 12, fill: COLOR_MUTED }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatCompactMoney}
                domain={[
                  dataMin => Math.min(dataMin, 0),
                  dataMax => Math.max(dataMax, 0),
                ]}
              />
              <Tooltip
                content={(
                  <CustomTooltip
                    includeIngresos={includeIngresos}
                    includeEgresos={includeEgresos}
                    includeLiquido={includeLiquido}
                    includePrevired={includePrevired}
                    includeF29={includeF29}
                  />
                )}
              />
              <Legend />

              <Line
                type='monotone'
                dataKey='presupuestoCajaProyectada'
                name='Presupuesto caja proyectada'
                stroke='url(#presupuestoCajaGradient)'
                strokeWidth={3}
                strokeDasharray='6 6'
                dot={<ProjectionDot />}
                activeDot={<ProjectionDot r={6} />}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <div className='flex min-h-[260px] items-center justify-center rounded-card border border-dashed border-border bg-border/10 px-6 text-center text-sm text-muted sm:min-h-[320px]'>
          Sin vencimientos futuros para mostrar
        </div>
      )}
    </DashboardCard>
  )
}

export default CashFlowDueLineChart
